// Anchor words for Russian noun cases — the thing a good teacher gives you
// INSTEAD of a table.
//
// Why not a table: reading a table is recognition, and recognition doesn't
// stick. Declining ONE known word per pattern until it is automatic, then
// reasoning "врач behaves like стол" is retrieval — and the same handful of
// anchors comes back all session, so the paradigm is absorbed rather than
// looked up. These seven cover the patterns a beginner meets.
//
// Every form below is hand-checked.

export const MODEL_WORDS = {
  'm-hard': {
    word: 'стол', en: 'table', desc: 'masculine, ending in a consonant',
    sg: { nominative: 'стол', accusative: 'стол', genitive: 'стола', dative: 'столу', instrumental: 'столом', prepositional: 'столе' },
    pl: { nominative: 'столы', accusative: 'столы', genitive: 'столов', dative: 'столам', instrumental: 'столами', prepositional: 'столах' },
  },
  'm-soft': {
    word: 'словарь', en: 'dictionary', desc: 'masculine, soft (-ь / -й)',
    sg: { nominative: 'словарь', accusative: 'словарь', genitive: 'словаря', dative: 'словарю', instrumental: 'словарём', prepositional: 'словаре' },
    pl: { nominative: 'словари', accusative: 'словари', genitive: 'словарей', dative: 'словарям', instrumental: 'словарями', prepositional: 'словарях' },
  },
  'f-hard': {
    word: 'школа', en: 'school', desc: 'feminine in -а',
    sg: { nominative: 'школа', accusative: 'школу', genitive: 'школы', dative: 'школе', instrumental: 'школой', prepositional: 'школе' },
    pl: { nominative: 'школы', accusative: 'школы', genitive: 'школ', dative: 'школам', instrumental: 'школами', prepositional: 'школах' },
  },
  'f-soft': {
    word: 'неделя', en: 'week', desc: 'feminine in -я',
    sg: { nominative: 'неделя', accusative: 'неделю', genitive: 'недели', dative: 'неделе', instrumental: 'неделей', prepositional: 'неделе' },
    pl: { nominative: 'недели', accusative: 'недели', genitive: 'недель', dative: 'неделям', instrumental: 'неделями', prepositional: 'неделях' },
  },
  'f-sign': {
    word: 'тетрадь', en: 'notebook', desc: 'feminine ending in -ь',
    sg: { nominative: 'тетрадь', accusative: 'тетрадь', genitive: 'тетради', dative: 'тетради', instrumental: 'тетрадью', prepositional: 'тетради' },
    pl: { nominative: 'тетради', accusative: 'тетради', genitive: 'тетрадей', dative: 'тетрадям', instrumental: 'тетрадями', prepositional: 'тетрадях' },
  },
  'n-hard': {
    word: 'окно', en: 'window', desc: 'neuter in -о',
    sg: { nominative: 'окно', accusative: 'окно', genitive: 'окна', dative: 'окну', instrumental: 'окном', prepositional: 'окне' },
    pl: { nominative: 'окна', accusative: 'окна', genitive: 'окон', dative: 'окнам', instrumental: 'окнами', prepositional: 'окнах' },
  },
  'n-soft': {
    word: 'море', en: 'sea', desc: 'neuter in -е',
    sg: { nominative: 'море', accusative: 'море', genitive: 'моря', dative: 'морю', instrumental: 'морем', prepositional: 'море' },
    pl: { nominative: 'моря', accusative: 'моря', genitive: 'морей', dative: 'морям', instrumental: 'морями', prepositional: 'морях' },
  },
}

// Which anchor a noun follows, from its dictionary form + gender.
export function modelKeyFor(baseNoun, gender) {
  const w = String(baseNoun || '').trim().toLowerCase()
  const last = w.slice(-1)
  if (gender === 'f') return last === 'ь' ? 'f-sign' : (last === 'я' ? 'f-soft' : 'f-hard')
  if (gender === 'n') return (last === 'е' || last === 'ё') ? 'n-soft' : 'n-hard'
  return (last === 'ь' || last === 'й') ? 'm-soft' : 'm-hard'
}

// The anchor's stem, so its ending can be read off cleanly.
const stemOf = (w) => String(w || '').replace(/[аяоеёыиуюьй]$/i, '')
export function modelEndingOf(model, caseKey, number) {
  const form = model?.[number]?.[caseKey]
  if (!form) return null
  const stem = stemOf(model.word)
  return form.toLowerCase().startsWith(stem.toLowerCase()) ? form.slice(stem.length) : null
}

// How much help each level gets. A beginner cannot "just remember" an ending
// they have never met; a B2 who is handed it never has to retrieve it.
export const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']
export const rungsForLevel = (lvl) =>
  ({ A0: 4, A1: 4, A2: 3, B1: 2, B2: 1, C1: 1, C2: 1 }[String(lvl || '').toUpperCase()] ?? 3)

export const capNoteForLevel = (lvl) =>
  rungsForLevel(lvl) <= 1
    ? 'At your level this one should come from memory — say the question word and trust your ear. (Lower your level in the setup if you want more scaffolding.)'
    : 'That is all the help for this one. Commit to an answer, then check — guessing and being corrected is what makes it stick.'

// The ladder of nudges for ONE item. Each rung narrows the search WITHOUT
// answering, so the learner is still the one producing the form.
//   1 the case question   2 the anchor word   3 the shortlist   4 the rule
// `trueEnding` is used only to keep the shortlist honest and to check that the
// anchor really does match — it is never printed.
export function buildLadder({ baseNoun, gender, number, caseKey, caseLabel, caseQuestion, trueEnding, chips = [], ruleText = '' }) {
  const key = modelKeyFor(baseNoun, gender)
  const model = MODEL_WORDS[key]
  const num = number === 'pl' ? 'pl' : 'sg'
  const modelForm = model?.[num]?.[caseKey] || null
  const modelEnding = modelEndingOf(model, caseKey, num)
  // Only offer the analogy when the anchor genuinely gives the same ending —
  // a misleading analogy is worse than none (спелling quirks after ц, ж, ш…).
  const analogyWorks = modelForm && modelEnding !== null && modelEnding === trueEnding
  const genderWord = { m: 'masculine', f: 'feminine', n: 'neuter' }[gender] || ''
  const numWord = num === 'pl' ? 'plural' : 'singular'

  const rungs = []

  rungs.push({
    key: 'question',
    heading: 'Start from the question',
    lines: [
      `The ${caseLabel} answers ${caseQuestion}`,
      `Say it out loud — «${caseQuestion.split('?')[0]}?» … ${baseNoun}… — and let the ending follow. Russians pick the ending by sound long before they think of a rule.`,
    ],
    speak: caseQuestion,
  })

  if (analogyWorks) {
    rungs.push({
      key: 'anchor',
      heading: `It behaves like ${model.word}`,
      lines: [
        `${baseNoun} is ${genderWord} ${numWord} — ${model.desc}. Your anchor for that pattern is ${model.word} (${model.en}).`,
        `You know this one: ${caseQuestion.split('?')[0]}? — ${modelForm}.`,
        `Do exactly the same to ${baseNoun}.`,
      ],
      speak: `${caseQuestion.split('?')[0]}? ${modelForm}`,
    })
  } else {
    rungs.push({
      key: 'shape',
      heading: 'Pin down what it is',
      lines: [
        `${baseNoun} is ${genderWord} ${numWord}.`,
        modelForm
          ? `Careful — this one does not follow ${model.word} exactly; a spelling rule bends it. Look at the letter just before the ending.`
          : 'Decide the gender and number first — the ending follows from those two facts plus the case.',
      ],
      speak: null,
    })
  }

  if (chips.length > 1) {
    rungs.push({
      key: 'shortlist',
      heading: 'It is one of these',
      lines: [`These are the endings ${genderWord} ${numWord} nouns take across the six cases. Only one of them belongs to the ${caseLabel} — which?`],
      chips,
      speak: null,
    })
  }

  if (ruleText) {
    rungs.push({
      key: 'rule',
      heading: 'The rule itself',
      lines: [`${caseLabel}, ${genderWord} ${numWord}: ${ruleText}`, 'Where two are shown, the bracketed one is the soft variant — after -ь, -й, -я or -е.'],
      speak: null,
    })
  }

  return rungs
}

export default MODEL_WORDS
